import {
  EDOCUMENT_PROVIDER_LABELS,
  edocumentProviderCodeSchema,
  INVOICE_SCENARIO_LABELS,
  INVOICE_SERIES_FAMILY_LABELS,
  type InvoiceSeries,
  type InvoiceSeriesFamily,
  invoiceSeriesFamilySchema,
  type InvoicingSettings,
  mockProviderOptionsSchema,
  PRICING_MODE_LABELS,
  pricingModeSchema,
  type TaxpayerLookup,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { SimpleTable, tdClass, thClass } from "../../../features/counterparties/shared.js";
import {
  useEdocumentProvider,
  useInvoiceSeries,
  useInvoicingSettings,
} from "../../../features/invoicing/queries.js";
import {
  InvoicingPage,
  useInvoicingCrumb,
  useInvoicingPermissions,
  useRefreshInvoicing,
} from "../../../features/invoicing/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";
import { useStepUp } from "../../../lib/step-up.js";

const TABS = ["general", "series", "provider"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/faturalar/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InvoicingSettingsPage,
});

function InvoicingSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const crumb = useInvoicingCrumb("/faturalar");
  const tab = Route.useSearch().tab ?? "general";

  return (
    <InvoicingPage
      title={t("menu.invoicingSettings")}
      description={t("invoicing.settings.description")}
      breadcrumb={[crumb, { label: t("menu.invoicingSettings") }]}
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({ search: (previous) => ({ ...previous, tab: next as TabKey }), replace: true })
          }
        >
          <TabsList aria-label={t("invoicing.settings.tabsLabel")}>
            {TABS.map((key) => (
              <TabsTrigger key={key} value={key}>
                {t(`invoicing.settings.tabs.${key}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="general">
            {tab === "general" ? <GeneralTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="series">
            {tab === "series" ? <SeriesTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="provider">
            {tab === "provider" ? <ProviderTab companyId={companyId} /> : null}
          </TabsContent>
        </Tabs>
      )}
    </InvoicingPage>
  );
}

// ── Genel ────────────────────────────────────────────────────────────────────
const DAY_FIELDS = [
  "defaultDueDays",
  "responseDeadlineDays",
  "responseAlertDays",
  "taxpayerCacheDays",
] as const;

function GeneralTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const canManage = useInvoicingPermissions().canManageSettings;
  // `invoices.settings.manage` step-up ister: oturum eskiyse doğrulama penceresi açılır ve işlem yinelenir.
  const withStepUp = useStepUp();
  const settings = useInvoicingSettings(companyId);
  const [draft, setDraft] = useState<Partial<InvoicingSettings>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoSendId = useId();
  const fetchId = useId();

  if (settings.isPending) return <SkeletonRows rows={6} />;
  if (settings.error) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;
  const current = { ...settings.data, ...draft };

  const invalid = DAY_FIELDS.filter((field) => {
    const value = current[field];
    if (!Number.isInteger(value)) return true;
    if (field === "responseDeadlineDays") return value < 1 || value > 60;
    if (field === "responseAlertDays") return value < 0 || value > 30;
    if (field === "taxpayerCacheDays") return value < 0 || value > 90;
    return value < 0 || value > 365;
  });

  async function submit(): Promise<void> {
    if (invalid.length > 0) return setError(t("invoicing.settings.invalidDays"));
    setBusy(true);
    setError(null);
    try {
      await withStepUp(() =>
        api.PATCH("/invoicing-settings", {
          params: { query: { companyId } },
          body: {
            defaultPricingMode: current.defaultPricingMode,
            defaultEinvoiceScenario: current.defaultEinvoiceScenario,
            defaultDueDays: current.defaultDueDays,
            responseDeadlineDays: current.responseDeadlineDays,
            responseAlertDays: current.responseAlertDays,
            taxpayerCacheDays: current.taxpayerCacheDays,
            autoSend: current.autoSend,
            incomingFetchEnabled: current.incomingFetchEnabled,
            version: current.version,
          },
        }),
      );
      await refresh(companyId);
      setDraft({});
      toast({ tone: "success", title: t("common.saved") });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="grid max-w-2xl gap-4 p-5">
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("invoicing.settings.defaultPricingMode")} required>
          {(control) => (
            <Select
              {...control}
              value={current.defaultPricingMode}
              disabled={!canManage}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  defaultPricingMode: pricingModeSchema.parse(event.target.value),
                }))
              }
            >
              {pricingModeSchema.options.map((mode) => (
                <option key={mode} value={mode}>
                  {PRICING_MODE_LABELS[mode]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField
          label={t("invoicing.settings.defaultEinvoiceScenario")}
          description={t("invoicing.settings.defaultEinvoiceScenarioHint")}
          required
        >
          {(control) => (
            <Select
              {...control}
              value={current.defaultEinvoiceScenario}
              disabled={!canManage}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  defaultEinvoiceScenario: event.target.value as "TEMELFATURA" | "TICARIFATURA",
                }))
              }
            >
              <option value="TEMELFATURA">{INVOICE_SCENARIO_LABELS.TEMELFATURA}</option>
              <option value="TICARIFATURA">{INVOICE_SCENARIO_LABELS.TICARIFATURA}</option>
            </Select>
          )}
        </FormField>
        {DAY_FIELDS.map((field) => (
          <FormField
            key={field}
            label={t(`invoicing.settings.fields.${field}`)}
            description={t(`invoicing.settings.hints.${field}`)}
            error={invalid.includes(field) ? t("invoicing.settings.invalidDays") : undefined}
            required
          >
            {(control) => (
              <Input
                {...control}
                className="tabular w-32"
                inputMode="numeric"
                disabled={!canManage}
                value={String(current[field])}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    [field]: Number(event.target.value.replace(/\D/g, "") || "0"),
                  }))
                }
              />
            )}
          </FormField>
        ))}
        <label className="flex items-center gap-2 text-sm" htmlFor={autoSendId}>
          <Checkbox
            id={autoSendId}
            checked={current.autoSend}
            disabled={!canManage}
            onChange={(event) => setDraft((previous) => ({ ...previous, autoSend: event.target.checked }))}
          />
          {t("invoicing.settings.autoSend")}
        </label>
        <label className="flex items-center gap-2 text-sm" htmlFor={fetchId}>
          <Checkbox
            id={fetchId}
            checked={current.incomingFetchEnabled}
            disabled={!canManage}
            onChange={(event) =>
              setDraft((previous) => ({ ...previous, incomingFetchEnabled: event.target.checked }))
            }
          />
          {t("invoicing.settings.incomingFetchEnabled")}
        </label>
        {canManage ? (
          <div className="flex justify-end">
            <Button type="submit" loading={busy}>
              {t("common.save")}
            </Button>
          </div>
        ) : (
          <Alert tone="info">{t("invoicing.settings.readOnly")}</Alert>
        )}
      </form>
    </Card>
  );
}

// ── Seriler ──────────────────────────────────────────────────────────────────
function SeriesTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const canManage = useInvoicingPermissions().canManageSettings;
  const withStepUp = useStepUp();
  const series = useInvoiceSeries(companyId);
  const [prefix, setPrefix] = useState("");
  const [family, setFamily] = useState<InvoiceSeriesFamily>("E_INVOICE");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultId = useId();

  async function create(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await withStepUp(() =>
        api.POST("/invoice-series", {
          body: {
            companyId,
            prefix: prefix.trim().toLocaleUpperCase("tr"),
            family,
            description: description.trim() || null,
            isDefault,
          },
        }),
      );
      await refresh(companyId);
      setPrefix("");
      setDescription("");
      toast({ tone: "success", title: t("invoicing.settings.seriesCreated") });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function patch(
    item: InvoiceSeries,
    body: { isDefault?: boolean; isActive?: boolean },
  ): Promise<void> {
    try {
      await withStepUp(() =>
        api.PATCH("/invoice-series/{id}", {
          params: { path: { id: item.id } },
          body: { ...body, version: item.version },
        }),
      );
      await refresh(companyId);
      toast({ tone: "success", title: t("common.saved") });
    } catch (caught) {
      toast({ tone: "danger", title: errorMessage(caught) });
    }
  }

  return (
    <div className="grid gap-4">
      {series.isPending ? (
        <SkeletonRows rows={4} />
      ) : series.error ? (
        <Alert tone="danger">{errorMessage(series.error)}</Alert>
      ) : (
        <SimpleTable
          caption={t("invoicing.settings.seriesCaption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("invoicing.settings.prefix")}
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.settings.family")}
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.settings.seriesDescription")}
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.settings.lastNumbers")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                <span className="sr-only">{t("common.actions")}</span>
              </th>
            </tr>
          }
        >
          {(series.data ?? []).map((item) => (
            <tr key={item.id} className="border-t">
              <td className={tdClass}>
                <span className="font-mono font-semibold">{item.prefix}</span>
              </td>
              <td className={tdClass}>{INVOICE_SERIES_FAMILY_LABELS[item.family]}</td>
              <td className={tdClass}>{item.description ?? "—"}</td>
              <td className={tdClass}>
                <span className="tabular text-xs">
                  {item.lastNumbers.length === 0
                    ? "—"
                    : item.lastNumbers.map((row) => `${row.year}: ${row.lastValue}`).join(" · ")}
                </span>
              </td>
              <td className={tdClass}>
                <span className="flex flex-wrap gap-1">
                  {item.isDefault ? (
                    <StatusBadge tone="success">{t("invoicing.settings.isDefault")}</StatusBadge>
                  ) : null}
                  <StatusBadge tone={item.isActive ? "info" : "neutral"}>
                    {item.isActive ? t("common.active") : t("common.inactive")}
                  </StatusBadge>
                </span>
              </td>
              <td className={tdClass}>
                {canManage ? (
                  <span className="flex gap-1">
                    {item.isDefault || !item.isActive ? null : (
                      <Button variant="ghost" size="sm" onClick={() => void patch(item, { isDefault: true })}>
                        {t("invoicing.settings.makeDefault")}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void patch(item, { isActive: !item.isActive })}
                    >
                      {item.isActive ? t("common.deactivate") : t("common.activate")}
                    </Button>
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}

      {canManage ? (
        <Card className="grid max-w-2xl gap-4 p-5">
          <h2 className="text-base font-semibold">{t("invoicing.settings.newSeries")}</h2>
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            {error ? <Alert tone="danger">{error}</Alert> : null}
            <div className="grid gap-3 sm:grid-cols-3">
              <FormField label={t("invoicing.settings.prefix")} description="ABC" required>
                {(control) => (
                  <Input
                    {...control}
                    className="font-mono uppercase"
                    maxLength={3}
                    value={prefix}
                    onChange={(event) => setPrefix(event.target.value.toLocaleUpperCase("tr"))}
                  />
                )}
              </FormField>
              <FormField label={t("invoicing.settings.family")} required>
                {(control) => (
                  <Select
                    {...control}
                    value={family}
                    onChange={(event) => setFamily(invoiceSeriesFamilySchema.parse(event.target.value))}
                  >
                    {invoiceSeriesFamilySchema.options.map((option) => (
                      <option key={option} value={option}>
                        {INVOICE_SERIES_FAMILY_LABELS[option]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("invoicing.settings.seriesDescription")}>
                {(control) => (
                  <Input
                    {...control}
                    maxLength={200}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                )}
              </FormField>
            </div>
            <label className="flex items-center gap-2 text-sm" htmlFor={defaultId}>
              <Checkbox
                id={defaultId}
                checked={isDefault}
                onChange={(event) => setIsDefault(event.target.checked)}
              />
              {t("invoicing.settings.makeDefaultOnCreate")}
            </label>
            <div className="flex justify-end">
              <Button type="submit" loading={busy} disabled={prefix.trim().length !== 3}>
                {t("common.add")}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Alert tone="info">{t("invoicing.settings.readOnly")}</Alert>
      )}
    </div>
  );
}

// ── Sağlayıcı + mükellef sorgusu ─────────────────────────────────────────────
const MOCK_NUMBER_FIELDS = ["stepSeconds", "failureRate", "failFirstAttempts", "incomingPerDay"] as const;
const MOCK_LIST_FIELDS = ["rejectTaxIds", "permanentFailTaxIds", "einvoiceTaxIds"] as const;

function ProviderTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const canManage = useInvoicingPermissions().canManageSettings;
  const withStepUp = useStepUp();
  const provider = useEdocumentProvider(companyId);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
    apiKey: string;
  } | null>(null);
  const [clearCredentials, setClearCredentials] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeId = useId();
  const incomingId = useId();

  if (provider.isPending) return <SkeletonRows rows={6} />;
  if (provider.error) return <Alert tone="danger">{errorMessage(provider.error)}</Alert>;

  const config = provider.data;
  const options = mockProviderOptionsSchema.parse({
    ...(config?.options ?? {}),
    ...((draft?.options as Record<string, unknown>) ?? {}),
  });
  const providerCode = (draft?.providerCode as string) ?? config?.providerCode ?? "MOCK";
  const isActive = (draft?.isActive as boolean) ?? config?.isActive ?? true;
  const einvoiceAlias = (draft?.einvoiceAlias as string) ?? config?.einvoiceAlias ?? "";
  const edespatchAlias = (draft?.edespatchAlias as string) ?? config?.edespatchAlias ?? "";

  const patchDraft = (patch: Record<string, unknown>) =>
    setDraft((previous) => ({ ...(previous ?? {}), ...patch }));
  const patchOptions = (patch: Record<string, unknown>) =>
    setDraft((previous) => ({
      ...(previous ?? {}),
      options: { ...(((previous ?? {}).options as Record<string, unknown>) ?? {}), ...patch },
    }));

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await withStepUp(() =>
        api.PUT("/edocument-provider", {
          params: { query: { companyId } },
          body: {
            providerCode: edocumentProviderCodeSchema.parse(providerCode),
            isActive,
            options: { ...options },
            einvoiceAlias: einvoiceAlias.trim() || null,
            edespatchAlias: edespatchAlias.trim() || null,
            // Kimlik bilgisi YAZMA-YALNIZDIR: verilmezse korunur, null verilirse silinir.
            ...(clearCredentials
              ? { credentials: null }
              : credentials && credentials.username && credentials.password
                ? {
                    credentials: {
                      username: credentials.username,
                      password: credentials.password,
                      apiKey: credentials.apiKey.trim() || null,
                    },
                  }
                : {}),
            ...(config ? { version: config.version } : {}),
          },
        }),
      );
      await refresh(companyId);
      setDraft(null);
      setCredentials(null);
      setClearCredentials(false);
      toast({ tone: "success", title: t("common.saved") });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <Card className="grid max-w-3xl gap-4 p-5">
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {error ? <Alert tone="danger">{error}</Alert> : null}
          {config ? null : <Alert tone="warning">{t("invoicing.settings.noProvider")}</Alert>}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label={t("invoicing.settings.providerCode")} required>
              {(control) => (
                <Select
                  {...control}
                  value={providerCode}
                  disabled={!canManage}
                  onChange={(event) => patchDraft({ providerCode: event.target.value })}
                >
                  {edocumentProviderCodeSchema.options.map((code) => (
                    <option key={code} value={code}>
                      {EDOCUMENT_PROVIDER_LABELS[code]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("invoicing.settings.einvoiceAlias")}>
              {(control) => (
                <Input
                  {...control}
                  maxLength={200}
                  disabled={!canManage}
                  value={einvoiceAlias}
                  onChange={(event) => patchDraft({ einvoiceAlias: event.target.value })}
                />
              )}
            </FormField>
            <FormField label={t("invoicing.settings.edespatchAlias")}>
              {(control) => (
                <Input
                  {...control}
                  maxLength={200}
                  disabled={!canManage}
                  value={edespatchAlias}
                  onChange={(event) => patchDraft({ edespatchAlias: event.target.value })}
                />
              )}
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm" htmlFor={activeId}>
            <Checkbox
              id={activeId}
              checked={isActive}
              disabled={!canManage}
              onChange={(event) => patchDraft({ isActive: event.target.checked })}
            />
            {t("invoicing.settings.providerActive")}
          </label>

          <fieldset className="grid gap-3 rounded-md border p-3">
            <legend className="px-1 text-sm font-semibold">{t("invoicing.settings.credentials")}</legend>
            <p className="text-xs text-muted-foreground">
              {config?.hasCredentials
                ? t("invoicing.settings.credentialsStored")
                : t("invoicing.settings.credentialsEmpty")}
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <FormField label={t("invoicing.settings.username")}>
                {(control) => (
                  <Input
                    {...control}
                    autoComplete="off"
                    disabled={!canManage || clearCredentials}
                    value={credentials?.username ?? ""}
                    onChange={(event) =>
                      setCredentials((previous) => ({
                        username: event.target.value,
                        password: previous?.password ?? "",
                        apiKey: previous?.apiKey ?? "",
                      }))
                    }
                  />
                )}
              </FormField>
              <FormField label={t("invoicing.settings.password")}>
                {(control) => (
                  <Input
                    {...control}
                    type="password"
                    autoComplete="new-password"
                    disabled={!canManage || clearCredentials}
                    value={credentials?.password ?? ""}
                    onChange={(event) =>
                      setCredentials((previous) => ({
                        username: previous?.username ?? "",
                        password: event.target.value,
                        apiKey: previous?.apiKey ?? "",
                      }))
                    }
                  />
                )}
              </FormField>
              <FormField label={t("invoicing.settings.apiKey")}>
                {(control) => (
                  <Input
                    {...control}
                    autoComplete="off"
                    disabled={!canManage || clearCredentials}
                    value={credentials?.apiKey ?? ""}
                    onChange={(event) =>
                      setCredentials((previous) => ({
                        username: previous?.username ?? "",
                        password: previous?.password ?? "",
                        apiKey: event.target.value,
                      }))
                    }
                  />
                )}
              </FormField>
            </div>
            {canManage && config?.hasCredentials ? (
              <div>
                <Button
                  variant={clearCredentials ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => setClearCredentials((previous) => !previous)}
                >
                  {clearCredentials
                    ? t("invoicing.settings.credentialsWillBeCleared")
                    : t("invoicing.settings.clearCredentials")}
                </Button>
              </div>
            ) : null}
          </fieldset>

          {providerCode === "MOCK" ? (
            <fieldset className="grid gap-3 rounded-md border p-3">
              <legend className="px-1 text-sm font-semibold">{t("invoicing.settings.mockOptions")}</legend>
              <div className="grid gap-3 sm:grid-cols-4">
                {MOCK_NUMBER_FIELDS.map((field) => (
                  <FormField key={field} label={t(`invoicing.settings.mock.${field}`)}>
                    {(control) => (
                      <Input
                        {...control}
                        className="tabular"
                        inputMode="numeric"
                        disabled={!canManage}
                        value={String(options[field])}
                        onChange={(event) =>
                          patchOptions({ [field]: Number(event.target.value.replace(/\D/g, "") || "0") })
                        }
                      />
                    )}
                  </FormField>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {MOCK_LIST_FIELDS.map((field) => (
                  <FormField
                    key={field}
                    label={t(`invoicing.settings.mock.${field}`)}
                    description={t("invoicing.settings.mock.listHint")}
                  >
                    {(control) => (
                      <Textarea
                        {...control}
                        rows={2}
                        disabled={!canManage}
                        value={(options[field] as string[]).join(", ")}
                        onChange={(event) =>
                          patchOptions({
                            [field]: event.target.value
                              .split(/[\s,;]+/)
                              .map((part) => part.trim())
                              .filter((part) => part !== ""),
                          })
                        }
                      />
                    )}
                  </FormField>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm" htmlFor={incomingId}>
                <Checkbox
                  id={incomingId}
                  checked={options.generateIncoming}
                  disabled={!canManage}
                  onChange={(event) => patchOptions({ generateIncoming: event.target.checked })}
                />
                {t("invoicing.settings.mock.generateIncoming")}
              </label>
            </fieldset>
          ) : null}

          {canManage ? (
            <div className="flex justify-end">
              <Button type="submit" loading={busy}>
                {t("common.save")}
              </Button>
            </div>
          ) : (
            <Alert tone="info">{t("invoicing.settings.readOnly")}</Alert>
          )}
        </form>
      </Card>

      <TaxpayerLookupTool companyId={companyId} />
    </div>
  );
}

/** Mükellef sorgusu aracı: VKN gir → e-Fatura / e-İrsaliye mükellefi mi, sonucun önbellekten gelip gelmediği. */
function TaxpayerLookupTool({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const [taxId, setTaxId] = useState("");
  const [result, setResult] = useState<TaxpayerLookup | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Card className="grid max-w-2xl gap-4 p-5">
      <h2 className="text-base font-semibold">{t("invoicing.settings.taxpayerLookup")}</h2>
      <form
        className="grid gap-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          setResult(null);
          api
            .POST("/taxpayer-lookups", { body: { companyId, taxId: taxId.trim(), refresh: false } })
            .then(({ data }) => setResult(data!))
            .catch((caught: unknown) => setError(errorMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex flex-wrap items-end gap-2">
          <FormField label={t("invoicing.settings.taxId")} className="w-48" required>
            {(control) => (
              <Input
                {...control}
                className="tabular"
                inputMode="numeric"
                maxLength={11}
                value={taxId}
                onChange={(event) => setTaxId(event.target.value.replace(/\D/g, ""))}
              />
            )}
          </FormField>
          <Button type="submit" loading={busy} disabled={taxId.trim().length < 10}>
            {t("invoicing.settings.lookup")}
          </Button>
        </div>
      </form>
      {result ? (
        <dl className="grid gap-1.5 text-sm" aria-live="polite">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t("invoicing.settings.title")}</dt>
            <dd className="font-medium">{result.title ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t("invoicing.settings.isEinvoiceUser")}</dt>
            <dd>
              <StatusBadge tone={result.isEinvoiceUser ? "success" : "neutral"}>
                {result.isEinvoiceUser ? t("common.yes") : t("common.no")}
              </StatusBadge>
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t("invoicing.settings.isEdespatchUser")}</dt>
            <dd>
              <StatusBadge tone={result.isEdespatchUser ? "success" : "neutral"}>
                {result.isEdespatchUser ? t("common.yes") : t("common.no")}
              </StatusBadge>
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t("invoicing.settings.checkedAt")}</dt>
            <dd className="tabular">
              {formatDateTime(result.checkedAt)}{" "}
              <span className="text-muted-foreground">
                {result.fromCache ? t("invoicing.settings.fromCache") : t("invoicing.settings.fresh")}
              </span>
            </dd>
          </div>
        </dl>
      ) : null}
    </Card>
  );
}
